import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Download, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import SettlementSync from './SettlementSync';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

export function AmazonReports({ storeId }) {
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  const [reportType, setReportType]       = useState('sales');
  const [groupBy, setGroupBy]             = useState('day');
  const [channelFilter, setChannelFilter] = useState('');
  const [loading, setLoading]             = useState(false);
  const [salesData, setSalesData]         = useState(null);
  const [inventoryData, setInventoryData] = useState(null);

  const formatMoney  = (v) => (parseFloat(v) || 0).toFixed(2);
  const formatNum    = (v) => parseInt(v) || 0;
  const channelLabel = channelFilter === 'MFN' ? 'FBM' : channelFilter === 'AFN' ? 'FBA' : 'All';

  const generateReport = async () => {
    if (reportType !== 'inventory' && (!startDate || !endDate)) {
      toast.error('Please select date range'); return;
    }
    setLoading(true);
    try {
      const token   = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const chParam = channelFilter ? `&fulfillment_channel=${channelFilter}` : '';

      if (reportType === 'inventory') {
        const fcParam = channelFilter ? `?fulfillment_channel=${channelFilter}` : '';
        const res  = await fetch(`${API_BASE_URL}/amazon/${storeId}/products${fcParam}`, { headers });
        const data = await res.json();
        if (data.success) {
          setInventoryData(data.products);
          setSalesData(null);
          toast.success('Inventory report generated!');
        } else toast.error('Failed to generate inventory report');
      } else {
        const res  = await fetch(
          `${API_BASE_URL}/amazon/${storeId}/reports/sales?start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}${chParam}`,
          { headers }
        );
        const data = await res.json();
        if (data.success) {
          setSalesData(data);
          setInventoryData(null);
          toast.success(`${reportType === 'sales' ? 'Sales' : 'Profit'} report generated!`);
        } else toast.error('Failed to generate report');
      }
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const exportCurrentReport = async () => {
    if (reportType !== 'inventory' && (!startDate || !endDate)) {
      toast.error('Please select date range'); return;
    }
    try {
      const token   = localStorage.getItem('token');
      const chParam = channelFilter ? `&fulfillment_channel=${channelFilter}` : '';

      if (reportType === 'inventory') {
        const headers = ['Product', 'ASIN', 'SKU', 'Channel', 'Status', 'Price', 'Cost', 'Stock'];
        const rows    = inventoryData.map(p => [
          `"${p.product_name || ''}"`, p.asin || '', p.seller_sku || '',
          p.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM',
          p.listing_status || 'Unknown',
          parseFloat(p.price || 0).toFixed(2), parseFloat(p.cost_price || 0).toFixed(2), p.quantity || 0
        ]);
        const csv  = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url  = window.URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `amazon-inventory-${channelLabel}-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(a); a.click(); a.remove();
        toast.success('CSV exported!'); return;
      }

      const res  = await fetch(
        `${API_BASE_URL}/amazon/${storeId}/reports/export?start_date=${startDate}&end_date=${endDate}${chParam}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `amazon-${reportType}-${channelLabel}-${startDate}-to-${endDate}.csv`);
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('CSV exported!');
    } catch { toast.error('Export failed'); }
  };

  const hasData = salesData || inventoryData;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Amazon Reports & Analytics</h1>

      {/* ✅ Settlement Sync — actual fees from Amazon */}
      <SettlementSync storeId={storeId} />

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Report Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">

          <div>
            <label className="block text-gray-700 mb-2 font-medium text-sm">Report Type</label>
            <select value={reportType}
              onChange={(e) => { setReportType(e.target.value); setSalesData(null); setInventoryData(null); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm">
              <option value="sales">Sales Report</option>
              <option value="profit">Profit Analysis</option>
              <option value="inventory">Inventory Report</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium text-sm">Channel</label>
            <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm">
              <option value="">All (FBA + FBM)</option>
              <option value="MFN">FBM only</option>
              <option value="AFN">FBA only</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium text-sm">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              disabled={reportType === 'inventory'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 text-sm" />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium text-sm">End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              disabled={reportType === 'inventory'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 text-sm" />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium text-sm">Group By</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              disabled={reportType === 'inventory'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 text-sm">
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button onClick={generateReport} disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all disabled:opacity-50 font-medium text-sm">
              {loading ? 'Generating...' : 'Generate'}
            </button>
            <button onClick={exportCurrentReport} disabled={!hasData}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export CSV">
              <Download size={18} />
            </button>
          </div>
        </div>

        {channelFilter && (
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
              channelFilter === 'AFN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
            }`}>
              Filtered: {channelLabel} only
              <button onClick={() => setChannelFilter('')} className="ml-1 hover:opacity-70">✕</button>
            </span>
          </div>
        )}
      </div>

      {/* Sales / Profit Report */}
      {salesData?.summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Orders</p>
              <p className="text-3xl font-bold text-orange-600">{formatNum(salesData.summary.total_orders)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Revenue</p>
              <p className="text-3xl font-bold text-green-600">£{formatMoney(salesData.summary.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Profit</p>
              <p className="text-3xl font-bold text-purple-600">£{formatMoney(salesData.summary.total_profit)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Profit Margin</p>
              <p className="text-3xl font-bold text-blue-600">
                {salesData.summary.total_revenue > 0
                  ? ((salesData.summary.total_profit / salesData.summary.total_revenue) * 100).toFixed(1) : '0.0'}%
              </p>
            </div>
          </div>

          {salesData.data?.length > 0 ? (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {reportType === 'sales' ? 'Sales Trend' : 'Profit Analysis'}
                {channelFilter ? ` — ${channelLabel}` : ''}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Revenue Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={salesData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(v) => `£${formatMoney(v)}`} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#ea580c" strokeWidth={2} name="Revenue" dot={{ r: 4 }} />
                      {reportType === 'profit' && (
                        <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2} name="Profit" dot={{ r: 4 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {reportType === 'sales' ? 'Order Volume' : 'Cost Breakdown'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={salesData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(v) => reportType === 'sales' ? v : `£${formatMoney(v)}`} />
                      <Legend />
                      {reportType === 'sales' ? (
                        <Bar dataKey="orders" fill="#ea580c" name="Orders" />
                      ) : (
                        <>
                          <Bar dataKey="revenue" fill="#ea580c" name="Revenue" />
                          <Bar dataKey="cost" fill="#ef4444" name="Cost" />
                          <Bar dataKey="fees" fill="#f59e0b" name="Fees" />
                        </>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg mt-6 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="text-left py-4 px-6 text-gray-600 font-semibold">Period</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Orders</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Units</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Revenue</th>
                        {reportType === 'profit' && (
                          <>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Fees</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Profit</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Margin</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.data.map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-800">{row.period}</td>
                          <td className="py-4 px-6 text-right">{formatNum(row.orders)}</td>
                          <td className="py-4 px-6 text-right">{formatNum(row.units_sold)}</td>
                          <td className="py-4 px-6 text-right font-semibold text-orange-600">£{formatMoney(row.revenue)}</td>
                          {reportType === 'profit' && (
                            <>
                              <td className="py-4 px-6 text-right text-red-600">£{formatMoney(row.cost)}</td>
                              <td className="py-4 px-6 text-right text-yellow-600">£{formatMoney(row.fees)}</td>
                              <td className="py-4 px-6 text-right font-bold text-green-600">£{formatMoney(row.profit)}</td>
                              <td className="py-4 px-6 text-right font-semibold text-purple-600">
                                {row.revenue > 0 ? ((row.profit / row.revenue) * 100).toFixed(1) : '0.0'}%
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="text-yellow-600 mr-3" size={24} />
                <div>
                  <h3 className="text-yellow-800 font-semibold">No Data Found</h3>
                  <p className="text-yellow-700">No orders found for the selected filters. Try a different range or channel.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Inventory Report */}
      {reportType === 'inventory' && inventoryData && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-2xl font-bold text-gray-800">
              Inventory Status {channelFilter ? `— ${channelLabel}` : ''}
            </h2>
            <div className="flex gap-3 text-sm flex-wrap">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                In Stock: {inventoryData.filter(p => p.quantity > 10).length}
              </span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                Low: {inventoryData.filter(p => p.quantity > 0 && p.quantity <= 10).length}
              </span>
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                Out: {inventoryData.filter(p => p.quantity === 0).length}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                FBA: {inventoryData.filter(p => p.fulfillment_channel === 'AFN').length}
              </span>
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                FBM: {inventoryData.filter(p => p.fulfillment_channel !== 'AFN').length}
              </span>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Product</th>
                    <th className="text-left py-3 px-4 text-gray-600 font-semibold">ASIN</th>
                    <th className="text-left py-3 px-4 text-gray-600 font-semibold">SKU</th>
                    <th className="text-center py-3 px-4 text-gray-600 font-semibold">Channel</th>
                    <th className="text-center py-3 px-4 text-gray-600 font-semibold">Status</th>
                    <th className="text-right py-3 px-4 text-gray-600 font-semibold">Price</th>
                    <th className="text-right py-3 px-4 text-gray-600 font-semibold">Cost</th>
                    <th className="text-right py-3 px-4 text-gray-600 font-semibold">Stock Value</th>
                    <th className="text-right py-3 px-6 text-gray-600 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryData.map((p, i) => {
                    const stockValue = parseFloat(p.cost_price || 0) * (p.quantity || 0);
                    return (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-6">
                          <p className="font-medium text-gray-800 truncate max-w-xs" title={p.product_name}>{p.product_name}</p>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs text-gray-600">{p.asin || '—'}</td>
                        <td className="py-3 px-4 text-xs text-gray-600">{p.seller_sku || '—'}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${p.fulfillment_channel === 'AFN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                            {p.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            p.listing_status === 'Active' ? 'bg-green-100 text-green-700' :
                            p.listing_status === 'Inactive' ? 'bg-yellow-100 text-yellow-700' :
                            p.listing_status === 'Incomplete' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                          }`}>{p.listing_status || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-orange-600">£{parseFloat(p.price || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-gray-600">£{parseFloat(p.cost_price || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-blue-600">£{stockValue.toFixed(2)}</td>
                        <td className="py-3 px-6 text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            p.quantity === 0 ? 'bg-red-100 text-red-700' :
                            p.quantity <= 10 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                          }`}>{p.quantity}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan={7} className="py-3 px-6 font-semibold text-gray-700">Total Stock Value</td>
                    <td className="py-3 px-4 text-right font-bold text-blue-600">
                      £{inventoryData.reduce((s, p) => s + parseFloat(p.cost_price || 0) * (p.quantity || 0), 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-6 text-right font-bold text-gray-700">
                      {inventoryData.reduce((s, p) => s + (p.quantity || 0), 0)} units
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AmazonReports;